import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { clamp, clamp01 } from '@/core/math/scalar';
import { circularVelocity, elementsFromState, apoapsisRadius } from '@/universe/OrbitalElements';
import { throttleGovernor } from '../ThrottleGovernor';
import { BaseMacro, MacroContext, MacroEstimate, MacroStatus, eastwardDirection } from './Macro';

/**
 * Launch to orbit.
 *
 * The macro flies a real ascent: vertical climb out of the thickest air, a
 * gravity turn whose pitch schedule is derived from altitude rather than from a
 * timer, engine cutoff when the predicted apoapsis reaches the target, a coast,
 * and a circularisation burn at apoapsis.
 *
 * Throughout, it respects dynamic pressure and hull temperature limits by
 * throttling back rather than by ignoring them, and it uses the field drive to
 * offset part of the vehicle's weight during the climb, which is precisely what
 * makes ascent from a terrestrial world affordable for a small vessel.
 */
export class LaunchToOrbitMacro extends BaseMacro {
  readonly id = 'launchToOrbit';
  readonly name = 'Launch to Orbit';
  readonly description =
    'Ascends from the surface to a circular orbit at the selected altitude, managing pitch, throttle, dynamic ' +
    'pressure and heating, then circularises at apoapsis.';
  readonly phases = ['Verify', 'Vertical climb', 'Gravity turn', 'Coast to apoapsis', 'Circularise', 'Complete'];

  /** Target circular orbit altitude above the datum, metres. */
  targetAltitude: number;

  private launchEast = new Vec3(1, 0, 0);
  private cutoffReached = false;

  constructor(targetAltitude = 200000) {
    super();
    this.targetAltitude = targetAltitude;
  }

  estimate(ctx: MacroContext): MacroEstimate {
    const { ship, body, env } = ctx;
    const rTarget = body.radius + this.targetAltitude;
    const vOrbit = circularVelocity(body.mu, rTarget);

    // Rotation gives a free head start on an eastward launch.
    const rotationBonus = body.surfaceVelocityAt(ship.body.position).length();

    // Gravity and drag losses, scaled by how much atmosphere there is to climb
    // through and how long the climb takes at the available acceleration.
    const g = env.gravity.length();
    const thrust = ship.mainEngines.reduce((s, e) => s + e.def.maxThrust, 0);
    const fieldAccel = ship.fieldDrive ? ship.fieldDrive.availableAcceleration(g) : 0;
    const mass = ship.massProperties.total;
    const netAccel = thrust / mass + fieldAccel - g;

    const failureModes = [
      'Insufficient thrust-to-weight for the climb',
      'Propellant exhausted before apoapsis',
      'Dynamic pressure exceeds airframe limit',
      'Hull temperature exceeds limit during ascent',
      'Field drive thermal cutoff',
    ];

    if (netAccel <= 0.2) {
      return {
        duration: Infinity,
        propellant: Infinity,
        deltaV: Infinity,
        peakAcceleration: thrust / mass,
        heatLoad: 0,
        failureModes,
        blocked: true,
        blockedReason: `Available acceleration (${(thrust / mass + fieldAccel).toFixed(2)} m/s^2) does not exceed local gravity (${g.toFixed(2)} m/s^2)`,
      };
    }

    const gravityLoss = g * (vOrbit / Math.max(1, netAccel)) * 0.42;
    const dragLoss = body.hasAtmosphere ? 90 + body.atmosphere.surfaceDensity * 260 : 0;
    const deltaV = Math.max(0, vOrbit - rotationBonus * 0.75) + gravityLoss + dragLoss;

    const available = ship.totalDeltaV();
    const duration = vOrbit / Math.max(0.5, netAccel) * 1.6 + 240;

    // Rocket-equation propellant estimate at the best available Isp.
    const isp = Math.max(1, ...ship.mainEngines.map((e) => e.def.ispVacuum));
    const ve = isp * 9.80665;
    const propellant = mass * (1 - Math.exp(-deltaV / ve));

    return {
      duration,
      propellant,
      deltaV,
      peakAcceleration: thrust / Math.max(1, mass - propellant),
      heatLoad: body.hasAtmosphere ? 1.83e-4 * Math.sqrt(body.atmosphere.surfaceDensity * 0.02) * 2200 ** 3 : 0,
      failureModes,
      blocked: deltaV > available,
      blockedReason:
        deltaV > available
          ? `Requires ${deltaV.toFixed(0)} m/s of delta-v; ${available.toFixed(0)} m/s available`
          : null,
    };
  }

  protected override onStart(ctx: MacroContext): void {
    this.launchEast = eastwardDirection(ctx.env);
    this.cutoffReached = false;
    ctx.ship.controls.mainEngineArmed = true;
    this.advance(1, 'Vertical climb');
  }

  protected tick(ctx: MacroContext): MacroStatus {
    const vitals = this.checkVitals(ctx);
    if (vitals) return this.fail(ctx, vitals);

    const { ship, computer, body, env } = ctx;
    const altitude = env.altitudeAboveDatum;
    const rTarget = body.radius + this.targetAltitude;

    // Back off if the airframe is being overstressed. The same limiter is
    // applied to manual flight by 'FlightComputer'; it is a ceiling, so the
    // computer taking the minimum of this and its own copy changes nothing.
    const governor = throttleGovernor(ship, this.safety.margin).limit;

    switch (this.phaseIndex) {
      case 1:
        return this.verticalClimb(ctx, altitude, governor);
      case 2:
        return this.gravityTurn(ctx, altitude, rTarget, governor);
      case 3:
        return this.coast(ctx, rTarget);
      case 4:
        return this.circularise(ctx, rTarget);
      default:
        computer.setAlignment('prograde');
        ship.controls.throttle = 0;
        ship.controls.mainEngineArmed = false;
        ship.controls.fieldDriveMode = 'off';
        this.statusText = 'Orbit achieved. Control returned to pilot.';
        return 'complete';
    }
  }

  /** Straight up until clear of the densest air, then start the turn. */
  private verticalClimb(ctx: MacroContext, altitude: number, governor: number): MacroStatus {
    const { ship, computer, body, env } = ctx;
    computer.setAlignment('surfaceUp');
    ship.controls.mainEngineArmed = true;
    ship.controls.throttle = governor;

    // The field drive carries as much of the weight as it can, which is the
    // whole reason a vessel this small can reach orbit at all.
    if (ship.fieldDrive) {
      ship.controls.fieldDriveMode = 'ascent';
      ship.controls.fieldDriveAscentAcceleration = 4;
    }

    // Turn-start altitude scales with atmosphere thickness: thin air means
    // there is no reason to waste time climbing vertically.
    const turnStart = body.hasAtmosphere ? Math.max(1500, body.atmosphere.scaleHeight * 0.9) : 400;
    const verticalSpeed = ship.body.velocity.dot(env.up);

    if (altitude > turnStart && verticalSpeed > 60) {
      this.advance(2, 'Gravity turn');
    }
    this.statusText = `Vertical climb: ${(altitude / 1000).toFixed(1)} km, ${verticalSpeed.toFixed(0)} m/s up`;
    return 'running';
  }

  /**
   * Pitch schedule as a function of altitude fraction through the atmosphere
   * (or through a nominal 60 km for airless bodies). The exponent shapes the
   * turn: lower values pitch over earlier and trade drag for gravity losses.
   */
  private gravityTurn(ctx: MacroContext, altitude: number, rTarget: number, governor: number): MacroStatus {
    const { ship, computer, body, env } = ctx;

    const turnCeiling = body.hasAtmosphere ? body.atmosphereTop * 0.85 : 60000;
    const progress = clamp01(altitude / Math.max(1, turnCeiling));
    const exponent = 0.62 + this.safety.margin * 0.35;
    const pitchFromVertical = (Math.PI / 2) * Math.pow(progress, exponent);

    const direction = new Vec3()
      .addScaled(env.up, Math.cos(pitchFromVertical))
      .addScaled(this.launchEast, Math.sin(pitchFromVertical))
      .normalize();

    computer.holds.alignment = 'custom';
    computer.customAttitude.copy(this.attitudeFor(direction, env.up));

    // Air-breathing engines are worth using while they still work.
    ship.controls.atmosphericEnginesArmed = env.density > 0.08;
    ship.controls.mainEngineArmed = true;
    ship.controls.throttle = governor;

    if (ship.fieldDrive) {
      // Field drive efficiency falls with altitude as gravity weakens, so it
      // naturally hands over to the main engine as the ascent proceeds.
      ship.controls.fieldDriveMode = 'ascent';
      ship.controls.fieldDriveAscentAcceleration = 2.5 * (1 - progress);
    }

    // Cut off when the predicted apoapsis reaches the target.
    const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, ctx.t);
    const apo = apoapsisRadius(elements);
    if (Number.isFinite(apo) && apo >= rTarget) {
      this.cutoffReached = true;
      this.advance(3, 'Coast to apoapsis');
      ship.controls.throttle = 0;
      ship.controls.mainEngineArmed = false;
      ship.controls.atmosphericEnginesArmed = false;
      ship.controls.fieldDriveMode = 'off';
      return 'running';
    }

    if (ship.massProperties.fuel <= 1) {
      return this.fail(ctx, 'Propellant exhausted during ascent');
    }

    this.statusText =
      `Gravity turn: ${(altitude / 1000).toFixed(1)} km, pitch ${((90 * (1 - pitchFromVertical / (Math.PI / 2))) | 0)} deg above horizon, ` +
      `apoapsis ${(Math.max(0, apo - body.radius) / 1000).toFixed(0)} km`;
    return 'running';
  }

  /** Coast to apoapsis, holding prograde and waiting out the atmosphere. */
  private coast(ctx: MacroContext, rTarget: number): MacroStatus {
    const { ship, computer, body } = ctx;
    computer.setAlignment('prograde');
    ship.controls.throttle = 0;
    ship.controls.mainEngineArmed = false;

    const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, ctx.t);
    const r = ship.body.position.length();
    const radialSpeed = ship.body.velocity.dot(ship.body.position.normalized());

    // Drag can rob apoapsis while coasting through the upper atmosphere; if the
    // target is no longer reachable, restart the burn rather than fail silently.
    const apo = apoapsisRadius(elements);
    if (Number.isFinite(apo) && apo < rTarget * 0.985 && ctx.env.density > 1e-6) {
      this.advance(2, 'Gravity turn (re-establishing apoapsis)');
      return 'running';
    }

    // Near apoapsis: radial velocity crosses zero.
    if (radialSpeed <= 0.5 || r >= rTarget * 0.995) {
      this.advance(4, 'Circularisation burn');
      return 'running';
    }

    this.statusText = `Coasting: ${(Math.max(0, r - body.radius) / 1000).toFixed(0)} km, closing on apoapsis`;
    return 'running';
  }

  /** Burn prograde at apoapsis until the orbit is circular. */
  private circularise(ctx: MacroContext, rTarget: number): MacroStatus {
    const { ship, computer, body } = ctx;
    computer.setAlignment('prograde');
    ship.controls.mainEngineArmed = true;

    const r = ship.body.position.length();
    const targetSpeed = circularVelocity(body.mu, r);
    const speed = ship.body.velocity.length();
    const remaining = targetSpeed - speed;

    if (ship.massProperties.fuel <= 1 && remaining > 5) {
      return this.fail(ctx, 'Propellant exhausted before circularisation');
    }

    if (remaining <= 0.6) {
      const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, ctx.t);
      const periAlt = elements.semiMajorAxis * (1 - elements.eccentricity) - body.radius;
      if (periAlt > body.atmosphereTop || !body.hasAtmosphere) {
        this.advance(5, 'Complete');
        return 'running';
      }
    }

    // Taper so the burn stops on target instead of overshooting.
    const accel = ship.lastThrustTotal / Math.max(1, ship.massProperties.total);
    const stopping = Math.max(0.5, accel * ctx.dt * 6);
    ship.controls.throttle = clamp(remaining / stopping, 0.06, 1);

    this.statusText = `Circularising: ${remaining.toFixed(1)} m/s remaining at ${((r - body.radius) / 1000).toFixed(0)} km`;
    void rTarget;
    return 'running';
  }

  /** Builds an attitude that points the nose along `direction`. */
  private attitudeFor(direction: Vec3, up: Vec3): Quat {
    const forward = direction.normalized();
    let reference = up;
    if (Math.abs(forward.dot(reference)) > 0.985) reference = Vec3.anyPerpendicular(forward);
    const right = Vec3.crossVectors(reference, forward).normalize();
    const trueUp = Vec3.crossVectors(forward, right).normalize();
    return Quat.fromBasis(right, trueUp, forward);
  }

  get reachedCutoff(): boolean {
    return this.cutoffReached;
  }
}
